/**
 * calcApi — client for the public equipment-cost calculators (PRP-CALC-1).
 *
 * These endpoints are PUBLIC (no auth): the /tools pages are free lead-gen
 * calculators, so this client uses `fetch` directly rather than the axios
 * BackendAPI instance (which injects a Firebase token and unwraps errors in a
 * way that hides the HTTP status we need to branch on for 422/429).
 *
 * Router prefix on the backend is `/api/v1/calc`; the shared BASE_URL already
 * ends in `/api/v1`, so each endpoint path here starts with `/calc/...`.
 */

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

// --- Request shapes -------------------------------------------------------

export type CraneSetupSurface = 'street' | 'lot' | 'soil';

export interface CraneRequest {
  pick_weight_lb: number;
  pick_radius_ft: number;
  tip_height_ft: number;
  pick_count: number;
  setup_surface: CraneSetupSurface;
  power_lines_within_20ft: boolean;
  occupied_building: boolean;
  picks_per_day_override?: number;
}

export type ScaffoldConfig = 'frame' | 'system';

export interface ScaffoldRequest {
  facade_lengths_ft: number[];
  height_ft: number;
  working_levels: number;
  duration_weeks: number;
  config: ScaffoldConfig;
  debris_netting: boolean;
}

export type LiftSurface = 'slab' | 'firm' | 'rough';

export interface LiftRequest {
  working_height_ft: number;
  horizontal_reach_ft?: number;
  indoor: boolean;
  surface: LiftSurface;
  duration_weeks: number;
}

// Water mitigation (IICRC S500). Provide area_sf OR length + width.
export type WaterClass = 1 | 2 | 3 | 4;
export type WaterCategory = 1 | 2 | 3;

export interface WaterMitigationRequest {
  length_ft?: number;
  width_ft?: number;
  area_sf?: number;
  ceiling_height_ft?: number;
  water_class: WaterClass;
  water_category: WaterCategory;
  chambers?: number;
  wall_insets?: number;
  affected_perimeter_lf?: number;
}

export type DrywallScope = 'walls' | 'ceiling' | 'both';
export type DrywallTexture =
  | 'none'
  | 'light_hand'
  | 'heavy_hand'
  | 'machine'
  | 'machine_knockdown'
  | 'smooth_skim';
export type DrywallActivity = 'replace' | 'patch';
export type DrywallThickness = '1/2' | '5/8';
export type DrywallWaterDamage = 'none' | 'clean' | 'cat3' | 'flood_nfip';
export type DrywallFloodCut = 'none' | '2ft' | '4ft';
export type BaseboardAction = 'none' | 'detach_reset' | 'remove_replace';
export type BaseboardSize = '2_1/4' | '3_1/4' | '5_1/4';
export type OpeningTrimAction =
  | 'replace'
  | 'detach_reset'
  | 'replace_prehung'
  | 'none';

export interface DrywallOpening {
  width_ft: number;
  height_ft: number;
  kind: 'door' | 'window';
  subtract_from_wall: boolean;
  trim_action: OpeningTrimAction;
}

export interface DrywallRoomRequest {
  length_ft: number;
  width_ft: number;
  height_ft?: number;
  openings: DrywallOpening[];
  scope: DrywallScope;
  thickness: DrywallThickness;
  texture: DrywallTexture;
  activity: DrywallActivity;
  patch_count?: number;
  paint_coats?: 1 | 2;
  water_damage: DrywallWaterDamage;
  flood_cut: DrywallFloodCut;
  baseboard_action: BaseboardAction;
  baseboard_size: BaseboardSize;
  floor_protection: boolean;
}

export type DumpsterSize = 12 | 20 | 30 | 40;

export interface DebrisRequest {
  roofing_squares?: number;
  roofing_layers?: number;
  drywall_sf?: number;
  carpet_sy?: number;
  framing_lf?: number;
  other_cy?: number;
  direct_cy?: number;
  dumpster_size_yd?: DumpsterSize;
  weight_limit_tons_override?: number;
}

// --- Response shapes ------------------------------------------------------

export interface CalcSource {
  source: string;
  doc: string;
  edition_date: string;
  region_basis: string;
  url: string;
}

export interface CalcLineItem {
  description: string;
  qty: number;
  unit: string;
  // Rates are null for quantity-only line items (price at import / quote
  // locally); the UI then shows a rate_basis chip instead of a dollar range.
  rate_low: number | null;
  rate_high: number | null;
  rate_unit: string;
  rate_basis: string;
  sources: CalcSource[];
  cat: string | null;
  sel: string | null;
  is_bid_item: boolean;
  // Xactimate line action, e.g. "Remove", "Replace", "Detach & reset". Null on
  // lines that carry no explicit action (a seal/prime coat, a mask-per-LF line).
  action: string | null;
  flags: string[];
  notes: string;
  // B-670 market band — the published "expect to pay" layer. Strictly
  // separate from rate_low/high (the ownership+operating justification
  // floor); the two are never blended into one number.
  market_low?: number | null;
  market_high?: number | null;
  market_unit?: string | null;
  market_qty?: number | null;
  market_note?: string | null;
  market_sources?: CalcSource[];
  // True when this line's cost is already inside another line's operated
  // market rate (e.g. the operator inside a crane-with-operator rate).
  market_covered?: boolean;
}

export interface CalcAssumption {
  key: string;
  value: string;
  basis: string;
}

export interface CalcResponse {
  summary: string;
  line_items: CalcLineItem[];
  narrative: string;
  assumptions: CalcAssumption[];
  disclaimers: string[];
  warnings: string[];
  // "priced" = commercial trio (reference rates ARE the product); the SEO trio
  // (drywall/water/debris) returns "quantities-only": real Xactimate items with
  // quantities and actions, no rates and no totals (Xactimate prices at import).
  pricing_mode: 'priced' | 'quantities-only';
  beyond_tool_range: boolean;
  using_mock_rates: boolean;
  total_low: number | null;
  total_high: number | null;
  adjusted_total_low?: number | null;
  adjusted_total_high?: number | null;
  // B-670: market "expect to pay" totals — the headline when present; the
  // O&O totals above become the justification footnote.
  market_total_low?: number | null;
  market_total_high?: number | null;
  adjusted_market_total_low?: number | null;
  adjusted_market_total_high?: number | null;
  copy_text: string;
}

/** An Error carrying the HTTP status so pages can branch on 422/429. */
export interface CalcError extends Error {
  status?: number;
}

async function postCalc<T>(
  path: string,
  body: T,
  regionalAdjustmentPct: number,
): Promise<CalcResponse> {
  const qs =
    regionalAdjustmentPct && regionalAdjustmentPct !== 0
      ? `?regional_adjustment_pct=${encodeURIComponent(regionalAdjustmentPct)}`
      : '';
  let resp: Response;
  try {
    resp = await fetch(`${BASE_URL}${path}${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    const err = new Error(
      'Network error. Check your connection and try again.',
    ) as CalcError;
    throw err;
  }

  if (!resp.ok) {
    // Reason: 422 (validation) and 429 (rate limit) drive distinct UX, so
    // carry the status through on the thrown error.
    const detail = await resp.json().catch(() => null);
    let message: string;
    if (resp.status === 429) {
      message = 'You are going a little fast. Try again in a minute.';
    } else if (resp.status === 422) {
      // FastAPI validation error shape: { detail: [{ loc, msg, ... }] }
      const first = Array.isArray(detail?.detail) ? detail.detail[0] : null;
      message = first?.msg
        ? `Please check your inputs: ${first.msg}`
        : 'Some inputs are out of range. Please review the form.';
    } else {
      message =
        (typeof detail?.detail === 'string' && detail.detail) ||
        `Request failed (${resp.status}).`;
    }
    const err = new Error(message) as CalcError;
    err.status = resp.status;
    throw err;
  }

  return (await resp.json()) as CalcResponse;
}

export const CalcAPI = {
  crane: (body: CraneRequest, regionalAdjustmentPct = 0) =>
    postCalc('/calc/crane', body, regionalAdjustmentPct),
  scaffold: (body: ScaffoldRequest, regionalAdjustmentPct = 0) =>
    postCalc('/calc/scaffold', body, regionalAdjustmentPct),
  lift: (body: LiftRequest, regionalAdjustmentPct = 0) =>
    postCalc('/calc/lift', body, regionalAdjustmentPct),
  waterMitigation: (body: WaterMitigationRequest, regionalAdjustmentPct = 0) =>
    postCalc('/calc/water-mitigation', body, regionalAdjustmentPct),
  drywallRoom: (body: DrywallRoomRequest, regionalAdjustmentPct = 0) =>
    postCalc('/calc/drywall-room', body, regionalAdjustmentPct),
  debris: (body: DebrisRequest, regionalAdjustmentPct = 0) =>
    postCalc('/calc/debris', body, regionalAdjustmentPct),
};

export default CalcAPI;
